import { useState } from "react";
import type { StopwatchState } from "../hooks/use-stopwatch";
import { TimerModule } from "../../../ui/molecules/TimerModule";

interface StopwatchTimerProps {
  readonly state: StopwatchState;
  readonly onStart: () => void;
  readonly onPause: () => void;
  readonly onReset: () => void;
  readonly onLap: () => void;
  readonly formatTime: (ms: number) => string;
  readonly onSettings: () => void;
}

function LapsPanel({
  laps,
  formatTime,
}: {
  readonly laps: ReadonlyArray<number>;
  readonly formatTime: (ms: number) => string;
}) {
  if (laps.length === 0) return null;

  return (
    <div className="px-3 py-2">
      <table className="w-full text-[11px] font-mono">
        <thead>
          <tr className="text-ink-muted/50 text-left">
            <th className="pb-1 pr-3 font-medium w-8">#</th>
            <th className="pb-1 pr-3 font-medium">Temps</th>
            <th className="pb-1 font-medium text-right">Delta</th>
          </tr>
        </thead>
        <tbody>
          {laps.map((lap, idx) => {
            const delta = idx === 0 ? lap : lap - laps[idx - 1];
            return (
              <tr key={idx} className="border-t border-border/30">
                <td className="py-0.5 pr-3 text-ink-muted/50">{idx + 1}</td>
                <td className="py-0.5 pr-3 text-accent/80">{formatTime(lap)}</td>
                <td className="py-0.5 text-right text-accent/50">+{formatTime(delta)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StopwatchTimeDisplay({ elapsed, formatTime }: {
  readonly elapsed: number;
  readonly formatTime: (ms: number) => string;
}) {
  const formatted = formatTime(elapsed);
  const dotIdx = formatted.lastIndexOf(".");
  if (dotIdx === -1) return <span>{formatted}</span>;
  return (
    <span>
      {formatted.slice(0, dotIdx)}
      <span className="text-xs md:text-base opacity-50">{formatted.slice(dotIdx)}</span>
    </span>

  );
}

export function StopwatchTimer({
  state,
  onStart,
  onPause,
  onReset,
  onLap,
  formatTime,
  onSettings,
}: StopwatchTimerProps) {
  const [showLaps, setShowLaps] = useState(false);
  const hasMillis = formatTime(0).includes(".");
  const sizeClass = hasMillis ? "text-xl md:text-2xl" : "text-xl md:text-3xl";

  return (
    <TimerModule
      isRunning={state.isRunning}
      timeDisplay={<StopwatchTimeDisplay elapsed={state.elapsed} formatTime={formatTime} />}
      onStart={onStart}
      onPause={onPause}
      onReset={onReset}
      onSettings={onSettings}
      timeSizeClass={sizeClass}
      settingsLabel="Réglages Chrono"
      info={
        <>
          <span className="text-xs font-semibold text-ink-muted uppercase tracking-widest">
            Chrono
          </span>
          {state.taskTitle && (
            <span className="text-xs font-medium text-accent truncate">{state.taskTitle}</span>
          )}
          {state.laps.length > 0 && (
            <button
              type="button"
              onClick={() => setShowLaps((v) => !v)}
              className="text-[10px] text-accent/60 hover:text-accent font-medium mt-0.5"
            >
              {state.laps.length} tour{state.laps.length > 1 ? "s" : ""} {showLaps ? "▴" : "▾"}
            </button>
          )}
        </>
      }
      extraActions={[
        {
          label: "+",
          ariaLabel: "Marquer un tour",
          onClick: onLap,
          disabled: !state.isRunning,
          className: "w-7 h-7 flex items-center justify-center text-accent hover:text-accent/80 hover:bg-accent/10 disabled:opacity-30 disabled:cursor-not-allowed rounded focus:outline-none focus:ring-2 focus:ring-accent",
        },
      ]}
      footer={showLaps ? (
        <div className="absolute top-full left-0 right-0 z-50 bg-surface border border-border border-t-0 shadow-lg max-h-48 overflow-y-auto">
          <LapsPanel laps={state.laps} formatTime={formatTime} />
        </div>
      ) : undefined}
    />
  );
}
